import { memo, useCallback, useEffect } from "react";
import Modal from "react-modal";
import PropTypes from "prop-types";
import { FLIGHT_OPTIONS_STATUS, INSURANCE_STANDARD } from "app/constants";
import FlexRateFlex from "app/pages/Booking/Quotation/FlexRate/FlexRateFlex";
import { calculateFlexIncluded } from "app/utils/quotationUtils";
import "./FlexRateModal.scss";
import useBlockViewportScroll from "app/pages/.shared/useBlockViewportScroll";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { CrossIcon } from "app/icons/navigation";

// la quote de ces évènements est spécifique : elle décrit le contenu de la quotation.
// Elle complète les données de quote injectées automatiquement par useAnalytics (fusion
// assurée par buildTagData), elle ne les remplace pas
const mapQuotationToQuoteTag = quotation => ({
	status: quotation.status,
	flightCount: quotation.flights ? String(quotation.flights.length) : "",
	accommodationCount: quotation.accommodations ? String(quotation.accommodations.length) : "",
	boardCount: quotation.boards ? String(quotation.boards.length) : "",
	transferCount: quotation.transfers ? String(quotation.transfers.length) : "",
	activityCount: quotation.activities ? String(quotation.activities.length) : "",
	quotePrice:
		quotation.quotationItems?.[0] && quotation.quotationItems?.[1]
			? String(quotation.quotationItems[0].price + quotation.quotationItems[1].price)
			: "",
});

const style = {
	overlay: {
		backgroundColor: "rgba(43, 56, 63, .9)",
		zIndex: 6, // 6 to be above holiday summary
	},
};

export const FlexRateModal = ({
	isOpened = false,
	quotation,
	submitting,
	handleSubmit,
	total,
	nbPassengers,
	updateBooking,
	showFlexRatesModal = () => {},
}) => {
	const { track } = useAnalytics();

	let flexInsurances = [];

	if (quotation.insurances) {
		flexInsurances = quotation.insurances.insurancesItem;
	}

	useBlockViewportScroll(isOpened);

	const handleFlexRatesClose = useCallback(() => {
		showFlexRatesModal(false);
	}, []);

	const handleClick = insurance => {
		updateBooking({
			insuranceFlex: { ...insurance, upgradePrice: totalFlex - total },
			insurance: {},
		});
		track("quotationPage_flex_select", {
			quote: mapQuotationToQuoteTag(quotation),
			flex: {
				standard_price: String(total),
				flex_price: String(totalFlex),
				flex_selected: String(isAssuranceFLex(insurance)),
			},
		});
		handleSubmit();
	};

	const getTotalFlex = insuranceItem => {
		const flexIncluded = calculateFlexIncluded(insuranceItem, total, nbPassengers);
		return total + flexIncluded;
	};

	const isAssuranceFLex = insurance => insurance.code !== INSURANCE_STANDARD;

	// ATTENTION partie assurance flex à retravailler si plus d'une assurance
	const totalFlex = flexInsurances
		? getTotalFlex(flexInsurances.find(insurance => isAssuranceFLex(insurance)))
		: 0;

	useEffect(() => {
		if (isOpened) {
			track("quotationPage_flex_overlay", {
				quote: mapQuotationToQuoteTag(quotation),
				flex: { standard_price: String(total), flex_price: String(totalFlex) },
			});
		}
	}, [isOpened]);

	return (
		<Modal
			style={style}
			isOpen={isOpened}
			onRequestClose={handleFlexRatesClose}
			className="flexrate__modal"
			overlayClassName="flexrate__overlay"
		>
			<div className="flexrate" data-testid="flex-rate-modal">
				<div className="flexrate__close" onClick={handleFlexRatesClose}>
					<CrossIcon className="icon icon--cross-dark" />
				</div>

				<div className="flexrate__rates">
					{flexInsurances &&
						flexInsurances.map(insurance => {
							return (
								<FlexRateFlex
									key={insurance.name}
									insurance={insurance}
									disabled={
										submitting ||
										quotation.flightOptionsStatus ===
											FLIGHT_OPTIONS_STATUS.LOADING
									}
									loading={
										submitting ||
										quotation.flightOptionsStatus ===
											FLIGHT_OPTIONS_STATUS.LOADING
									}
									handleClick={handleClick}
									total={totalFlex}
									upgradePrice={totalFlex - total}
								/>
							);
						})}
				</div>
			</div>
		</Modal>
	);
};

FlexRateModal.propTypes = {
	isOpened: PropTypes.bool,
	quotation: PropTypes.object,
	handleSubmit: PropTypes.func,
	submitting: PropTypes.bool,
	total: PropTypes.number,
	nbPassengers: PropTypes.number,
	updateBooking: PropTypes.func,
	showFlexRatesModal: PropTypes.func,
};

export default memo(FlexRateModal);
